import type { CreativeAxis, CreativeBrandPosition } from './creativeLandscapeData.schema';

/**
 * Scores run -1..1, but the plot is drawn over -1.2..1.2 so a brand scored at either extreme
 * sits inside the frame instead of half-clipped by its edge.
 */
const AXIS_MIN = -1.2;
const AXIS_SPAN = 2.4;

export const AXIS_EXTENT = 1.2;

export interface DotPlacement {
  /** Percentages, so the plot can be sized in CSS without the model knowing its pixels. */
  leftPercent: number;
  topPercent: number;
}

// The read side does not reject an out-of-range score, because failing the parse would blank
// the section over one bad number. Clamping keeps such a dot on the frame edge, where the plot
// clips it away.
const clamp = (value: number) => Math.min(AXIS_EXTENT, Math.max(-AXIS_EXTENT, value));

/** Y is inverted: SVG-less absolute positioning grows downward, the axis grows upward. */
export function dotPlacement(position: Pick<CreativeBrandPosition, 'x' | 'y'>): DotPlacement {
  return {
    leftPercent: ((clamp(position.x) - AXIS_MIN) / AXIS_SPAN) * 100,
    topPercent: ((AXIS_EXTENT - clamp(position.y)) / AXIS_SPAN) * 100,
  };
}

/** The end of the axis a score falls on, so a number can be read as a direction. */
export function axisSideLabel(value: number, axis: CreativeAxis): string {
  return value >= 0 ? axis.highLabel : axis.lowLabel;
}

/**
 * Competitors scoring higher than the selected brand on one axis, strongest first. The design
 * shows these under the positioning sentence to say who out-indexes this brand on each axis.
 */
export function higherOnAxis(
  brands: CreativeBrandPosition[],
  advertiser: string,
  axis: 'x' | 'y',
): string[] {
  const selected = brands.find((b) => b.advertiser === advertiser);
  if (!selected) return [];
  return brands
    .filter((b) => b.advertiser !== advertiser && b[axis] > selected[axis])
    .sort((a, b) => b[axis] - a[axis])
    .map((b) => b.advertiser);
}
